import { useEffect, useRef, useState } from 'react';
import suneditor from 'suneditor';
import plugins from 'suneditor/src/plugins';
import ko from 'suneditor/src/lang/ko';
import type SunEditorCore from 'suneditor/src/lib/core';
import 'suneditor/dist/css/suneditor.min.css';
import { sanitizeLikeStorefront } from '@/lib/storefrontHtml';
import { hasInlineImage, replaceInlineImages, stripInlineImages } from './richTextImages';
import { normalizeEditorOutput } from './richTextOutput';
import { wrapMultiImageTopBlocks } from './richTextInput';

export interface RichTextEditorProps {
  /** Current HTML value. */
  value: string;
  /** Called with the editor HTML on every change (empty editor normalised to ''). */
  onChange: (html: string) => void;
  placeholder?: string;
  /** Minimum editable body height in px. Default 340. */
  minHeight?: number;
  /**
   * Upload handler for images (toolbar, paste, drag & drop). Receives the File and must resolve to a public URL
   * (e.g. presign → S3 PUT → confirm). When omitted, images can only be inserted by URL.
   */
  onUploadImage?: (file: File) => Promise<string>;
  /** Called when an image upload throws, so the host can show a message. */
  onUploadError?: (error: unknown) => void;
  /**
   * Shows the HTML source (code view) button. The output is raw HTML; the storefront sanitises on render.
   * Defaults to true.
   */
  allowHtmlSource?: boolean;
  /** 본문 이미지 업로드 안내(권장 폭·형식·용량). lib/upload editorImageGuideFor(surface) 로 만든다. */
  imageGuide?: string;
  'aria-label'?: string;
}

/** insertImagesAtCaret 가 쓰는 SunEditor 기능만 — 테스트에서 실제 SunEditor 를 그대로 넘긴다. */
export interface CaretImageEditor {
  core: {
    setRange(startCon: Node, startOff: number, endCon: Node, endOff: number): unknown;
    context: { element: { wysiwyg: Element } };
  };
  insertHTML(html: string, notCleaningData?: boolean): void;
}

/**
 * QA-477 — 올린 이미지를 커서 자리에 넣는다. 이미지 대화상자로 포커스가 가면 SunEditor 는 커서를 잃고 맨 아래로 넣으므로,
 * 기억해 둔 커서를 되돌린 뒤 SunEditor 의 이미지 덩어리(.se-component)로 넣는다 — 그 줄을 커서에서 나눠 사이에 끼운다.
 *
 * 종전엔 맨 <img> 를 줄 안에 바로 넣었는데, SunEditor 가 나중에 덩어리로 바꾸면서 커서가 덩어리 안에 남아 그 안에만 글이
 * 써지고(점선 영역), Enter·Ctrl+C/X 가 먹지 않았고, 본문 끝이면 이미지 아래에 글을 쓸 줄이 없었다(26.10.22 추가 현상).
 * 그래서 마지막 이미지 아래에 쓸 줄이 없으면 빈 줄을 만들고 커서를 그 줄로 옮긴다.
 */
export const insertImagesAtCaret = (
  editor: CaretImageEditor,
  range: Range,
  images: ReadonlyArray<{ url: string; name?: string }>,
): HTMLElement | null => {
  const wysiwyg = editor.core.context.element.wysiwyg as HTMLElement;
  const doc = wysiwyg.ownerDocument;
  if (images.length === 0) return null;
  editor.core.setRange(range.startContainer, range.startOffset, range.endContainer, range.endOffset);
  // 여러 장은 한 번에 넣는다 — 한 장씩 넣으면 SunEditor 가 두 번째부터 본문 맨 위로 넣는다.
  const html = images
    .map((img) => {
      const el = doc.createElement('img');
      el.setAttribute('src', img.url);
      el.setAttribute('alt', img.name ?? '');
      return el.outerHTML;
    })
    .join('');
  editor.insertHTML(html, true);
  const last = images[images.length - 1]!;
  const inserted = Array.from(wysiwyg.querySelectorAll('img')).filter((i) => i.getAttribute('src') === last.url).pop();
  if (!inserted) return null;
  const block = inserted.closest('.se-component') as HTMLElement | null;
  // 덩어리로 바뀌지 않은 경우(줄 안 그림)는 그 뒤에 커서만 둔다 — 줄 안에 <p> 를 끼우면 구조가 깨진다.
  if (!block) {
    const parent = inserted.parentNode!;
    const at = Array.prototype.indexOf.call(parent.childNodes, inserted) + 1;
    editor.core.setRange(parent, at, parent, at);
    return inserted;
  }
  let line = block.nextElementSibling as HTMLElement | null;
  if (!line || line.classList.contains('se-component') || line.tagName === 'TABLE') {
    line = doc.createElement('p');
    line.appendChild(doc.createElement('br'));
    block.after(line);
  }
  editor.core.setRange(line, 0, line, 0);
  return block;
};

/** 이미지 업로드를 함께 다루지 않는 단순 화면에서 쓸 버튼(이미지는 URL 입력). */
const buttonsFor = (allowHtmlSource: boolean) => [
  ['undo', 'redo'],
  ['formatBlock', 'fontSize'],
  ['bold', 'underline', 'italic', 'strike'],
  ['fontColor', 'hiliteColor', 'removeFormat'],
  ['align', 'list', 'lineHeight', 'horizontalRule', 'blockquote'],
  ['table', 'link', 'image', 'video'],
  allowHtmlSource ? ['codeView', 'fullScreen'] : ['fullScreen'],
];

/**
 * QA-335 — 공용 HTML 에디터. SunEditor 2(iframe 없이 contenteditable)로 운영 HTML(표·div·인라인 스타일, 고도몰 이관 공통
 * 안내 표 등)을 원본 그대로 보여 주고 편집한다(고도몰 SmartEditor2 와 같은 방식). 종전 TipTap 은 정해진 태그만 알아서
 * 표·div·블록 스타일이 줄글로 무너지고, 거기서 고치면 사라진 채 저장됐다.
 *
 * - 값은 value/onChange 로 제어한다. 저장값은 raw HTML(정렬은 FE 와 맞춰 class 로 — richTextOutput).
 * - 이미지(버튼·붙여넣기·끌어다 놓기)는 onUploadImage 로 S3 에 올린 주소를 넣는다. base64 이미지는 저장하지 않는다.
 * - [FE 미리보기]는 FE 와 같은 sanitize 규칙으로 그린다(테두리·여백·정렬 같은 레이아웃 스타일은 FE 에서 빠진다).
 */
export const RichTextEditor = ({
  value,
  onChange,
  placeholder = '본문을 입력하세요.',
  minHeight = 340,
  onUploadImage,
  onUploadError,
  allowHtmlSource = true,
  imageGuide,
  'aria-label': ariaLabel,
}: RichTextEditorProps) => {
  const hostRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<SunEditorCore | null>(null);
  // 마지막으로 바깥에 내보낸 값 — 같은 값이 다시 들어오면 setContents 하지 않는다(커서 튐 방지).
  const emittedRef = useRef<string>(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const uploadRef = useRef(onUploadImage);
  uploadRef.current = onUploadImage;
  const uploadErrorRef = useRef(onUploadError);
  uploadErrorRef.current = onUploadError;
  const [notice, setNotice] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
  const [fePreview, setFePreview] = useState(false);
  // SunEditor 가 HTML 을 열지 못하면(내부 오류) HTML 직접 편집으로 바꾼다 — 상품 화면 전체가 죽지 않게.
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let editor: SunEditorCore;
    try {
      editor = createEditor(host);
    } catch (err) {
      // 에디터가 이 HTML 을 열지 못해도 화면 전체가 죽지 않게 — HTML 직접 편집으로 바꾼다.
      host.parentElement?.querySelectorAll('.sun-editor').forEach((el) => el.remove());
      console.error('[RichTextEditor] SunEditor 를 열지 못해 HTML 편집으로 바꿉니다.', err);
      setFallback(true);
      return;
    }
    function createEditor(target: HTMLTextAreaElement): SunEditorCore {
      return suneditor.create(target, {
      lang: ko,
      plugins: plugins as never,
      buttonList: buttonsFor(allowHtmlSource) as never,
      width: '100%',
      height: 'auto',
      minHeight: `${minHeight}px`,
      placeholder,
      // 운영 HTML 을 그대로 — 스타일·class 를 정리하지 않고, 표·div 등 태그를 지우지 않는다(script 는 SunEditor 가 계속 막는다).
      strictMode: false,
      addTagsWhitelist: 'div|span|table|thead|tbody|tfoot|tr|td|th|colgroup|col|caption|section|article|header|footer|figure|figcaption|center|font|dl|dt|dd|iframe|video|source|blockquote|h1|h2|h3|h4|h5|h6|br|hr|mark|small|sup|sub',
      attributesWhitelist: {
        all: 'style|class|id|title|alt|width|height|align|valign|colspan|rowspan|border|cellpadding|cellspacing|bgcolor|color|face|size|target|rel|href|src|frameborder|allow|allowfullscreen|data-.+',
      },
      imageAccept: '.jpg,.jpeg,.png,.gif,.webp',
      imageMultipleFile: true,
      resizingBar: true,
      charCounter: false,
      defaultStyle: 'font-size:14px; line-height:1.7;',
      // 최상위 <center>·<font> 등에 이미지가 여러 장이면 SunEditor 가 처음 열 때 죽는다 — <div> 로 감싸 넣는다.
      value: wrapMultiImageTopBlocks(value),
    });
    }
    editorRef.current = editor;
    emittedRef.current = value;

    const emit = (raw: string) => {
      // 직접 넣은 base64 이미지 데이터는 저장하지 않는다(올리기는 붙여넣기 처리에서).
      const cleaned = hasInlineImage(raw) ? stripInlineImages(raw) : raw;
      const out = normalizeEditorOutput(cleaned);
      emittedRef.current = out;
      onChangeRef.current(out);
    };

    editor.onChange = (contents) => emit(contents);

    // QA-477 — 본문 안 마지막 커서 위치. 이미지 버튼을 누르면 포커스가 대화상자로 가므로 그 직전 위치를 기억한다.
    const wysiwyg = editor.core.context.element.wysiwyg as HTMLElement | undefined;
    let lastRange: Range | null = null;
    const rememberCaret = () => {
      const sel = document.getSelection();
      if (!sel || sel.rangeCount === 0 || !wysiwyg) return;
      const r = sel.getRangeAt(0);
      if (wysiwyg.contains(r.startContainer)) lastRange = r.cloneRange();
    };
    document.addEventListener('selectionchange', rememberCaret);
    wysiwyg?.addEventListener('keyup', rememberCaret);
    wysiwyg?.addEventListener('mouseup', rememberCaret);
    // HTML 소스(코드 보기)에서 타이핑해도 바로 저장값에 반영한다(서식 보기로 돌아오지 않고 저장하는 경우).
    const code = editor.core.context.element.code as HTMLTextAreaElement | undefined;
    const onCodeInput = () => emit(code?.value ?? '');
    code?.addEventListener('input', onCodeInput);

    // 이미지 버튼·끌어다 놓기·이미지 파일 붙여넣기 → S3 업로드 후 그 주소를 넣는다.
    editor.onImageUploadBefore = (files, info, _core, uploadHandler) => {
      const upload = uploadRef.current;
      if (!upload) {
        uploadHandler('이미지 업로드를 쓸 수 없는 화면입니다. 이미지 주소(URL)로 넣어 주세요.');
        return false;
      }
      // 새 이미지이고 본문 안 커서를 알면 그 자리에(QA-477). 기존 이미지 바꾸기·커서를 모르면 SunEditor 기본 동작.
      const isUpdate = (info as { isUpdate?: boolean } | undefined)?.isUpdate === true;
      const caret = !isUpdate && lastRange && wysiwyg?.contains(lastRange.startContainer) ? lastRange.cloneRange() : null;
      void (async () => {
        try {
          const result = [];
          for (const file of files as File[]) {
            result.push({ url: await upload(file), name: file.name, size: file.size });
          }
          if (caret) {
            insertImagesAtCaret(editor, caret, result);
            editor.core.closeLoading();
            editor.core.history.push(false);
            emit(editor.getContents(true));
            return;
          }
          uploadHandler({ result });
        } catch (err) {
          uploadErrorRef.current?.(err);
          uploadHandler('이미지를 올리지 못했습니다. 다시 시도해 주세요.');
        }
      })();
      return undefined;
    };

    // base64 이미지가 섞인 HTML 붙여넣기 → 파일로 올려 주소로 바꿔 넣는다(못 올린 것은 빼고 안내).
    editor.onPaste = (_e, cleanData) => {
      if (!hasInlineImage(cleanData)) return true;
      const upload = uploadRef.current;
      if (!upload) {
        setNotice({ tone: 'error', text: '붙여넣은 이미지는 저장할 수 없어 뺐습니다. 이미지 버튼으로 올려 주세요.' });
        return stripInlineImages(cleanData);
      }
      void replaceInlineImages(cleanData, upload).then((res) => {
        editor.insertHTML(res.html, true);
        setNotice(
          res.dropped > 0
            ? { tone: 'error', text: '일부 이미지는 붙여넣을 수 없어 빠졌습니다. 이미지 버튼으로 다시 올려 주세요.' }
            : { tone: 'info', text: `붙여넣은 이미지 ${res.uploaded}개를 파일로 올렸습니다.` },
        );
      });
      return false;
    };

    if (ariaLabel) wysiwyg?.setAttribute('aria-label', ariaLabel);

    return () => {
      code?.removeEventListener('input', onCodeInput);
      document.removeEventListener('selectionchange', rememberCaret);
      wysiwyg?.removeEventListener('keyup', rememberCaret);
      wysiwyg?.removeEventListener('mouseup', rememberCaret);
      editor.destroy();
      editorRef.current = null;
    };
    // 에디터는 한 번 만든다. 옵션 변경(높이·소스 버튼)은 화면에서 바뀌지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 바깥에서 들어온 값(상품 불러오기·로케일 전환 등)만 에디터에 다시 넣는다.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || value === emittedRef.current) return;
    emittedRef.current = value;
    try {
      editor.setContents(wrapMultiImageTopBlocks(value || ''));
    } catch (err) {
      console.error('[RichTextEditor] 내용을 에디터에 넣지 못해 HTML 편집으로 바꿉니다.', err);
      setFallback(true);
    }
  }, [value]);

  return (
    <div className="flex flex-col" data-testid="rich-text-editor">
      {notice ? (
        <div
          role={notice.tone === 'error' ? 'alert' : 'status'}
          className="flex items-start justify-between gap-2 rounded-t-md px-3 py-2 text-[12px]"
          style={
            notice.tone === 'error'
              ? { background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }
              : { background: 'var(--status-info-bg, var(--surface-1))', color: 'var(--content-default)' }
          }
          data-testid="rte-notice"
        >
          <span>{notice.text}</span>
          <button type="button" className="shrink-0 underline" onClick={() => setNotice(null)}>
            닫기
          </button>
        </div>
      ) : null}
      <textarea ref={hostRef} defaultValue={value} hidden readOnly aria-hidden="true" />
      {fallback ? (
        <div className="flex flex-col gap-1" data-testid="rte-fallback">
          <p role="alert" className="rounded-t-md px-3 py-2 text-[12px]" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
            에디터로 열 수 없는 HTML 구조라 HTML 로 직접 편집합니다. 저장하면 그대로 반영됩니다.
          </p>
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={18}
            aria-label={ariaLabel ?? 'HTML 편집'}
            className="w-full rounded-md px-3 py-2 font-mono text-[12px]"
            style={{ border: '1px solid var(--divider-strong)', background: 'var(--surface-0)', color: 'var(--content-strong)' }}
          />
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2 px-1 pt-1.5 text-[11px]" style={{ color: 'var(--content-muted)' }}>
        {onUploadImage && imageGuide ? <span data-testid="rte-image-guide">{imageGuide}</span> : <span />}
        <button type="button" className="shrink-0 underline" onClick={() => setFePreview((v) => !v)} data-testid="rte-fe-preview-toggle">
          {fePreview ? 'FE 미리보기 닫기' : 'FE 미리보기'}
        </button>
      </div>
      {fePreview ? (
        <div className="mt-1 rounded-md border" style={{ borderColor: 'var(--divider-strong)' }}>
          <p className="border-b px-3 py-1.5 text-[11px]" style={{ color: 'var(--content-muted)', borderColor: 'var(--divider-soft)' }}>
            FE 와 같은 규칙으로 그린 모습입니다 — 테두리·여백·정렬·폭 같은 레이아웃 스타일은 FE 에서 빠집니다.
          </p>
          <div
            className="tiptap-content overflow-x-auto"
            style={{ padding: '14px 16px', fontSize: '14px', lineHeight: 1.7, color: 'var(--content-strong)' }}
            data-testid="rte-html-preview"
            dangerouslySetInnerHTML={{ __html: sanitizeLikeStorefront(value) }}
          />
        </div>
      ) : null}
    </div>
  );
};
